// src/pages/PrivacyPolicy.tsx
import React, { useState, useEffect } from "react";
import { PageHeader } from "../components/PageHeader.tsx";
import "./PrivacyPolicy.css";

export const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("");

  const sections = [
    { id: "introduction", title: "1. Introduction" },
    { id: "information-collected", title: "2. Information We Collect" },
    { id: "how-we-use", title: "3. How We Use Your Information" },
    { id: "advertising", title: "4. Advertising Partners" },
    { id: "legal-basis", title: "5. Legal Basis for Processing" },
    { id: "sharing", title: "6. How We Share Information" },
    { id: "international", title: "7. International Transfers" },
    { id: "retention", title: "8. Data Retention" },
    { id: "your-rights", title: "9. Your Rights" },
    { id: "us-privacy", title: "10. US State Privacy Rights" },
    { id: "cookies", title: "11. Cookies and Tracking" },
    { id: "dnt", title: "12. Do-Not-Track" },
    { id: "age", title: "13. Age Requirements" },
    { id: "security", title: "14. Security Measures" },
    { id: "other-regions", title: "15. Other Regional Rights" },
    { id: "changes", title: "16. Changes to Policy" },
    { id: "contact", title: "17. Contact Us" }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      }));

      for (const section of sectionElements) {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="privacy-page">
      <PageHeader
        icon="🔒"
        title="Privacy Policy"
        description="How we collect, use, and protect your personal information"
      />

      <div className="privacy-content">
        {/* Table of Contents - Sidebar */}
        <nav className="privacy-nav">
          <div className="nav-sticky">
            <h3 className="nav-title">Contents</h3>
            <ul className="nav-list">
              {sections.map(section => (
                <li key={section.id}>
                  <button
                    className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main Content */}
        <main className="privacy-main">
          <div className="policy-card">
            {/* Print Button - Top */}
            <div className="policy-header-actions">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                🖨️ Print / Save as PDF
              </button>
            </div>

            {/* Last Updated Banner */}
            <div className="last-updated">
              <span className="update-icon">📅</span>
              <span><strong>Last Updated:</strong> January 07, 2026</span>
            </div>

            {/* Plain Language Summary */}
            <div className="summary-box">
              <h2>📋 Summary</h2>
              <p>
                This Privacy Policy explains how Expanded Chess ("we," "us," "our") collects,
                uses, stores, and protects your personal information when you use Expanded Chess
                with advertising enabled. We are committed to protecting your privacy and being
                transparent about our data practices, including our use of advertising partners
                and marketing cookies. <strong>We do not process sensitive personal information.</strong>
              </p>
            </div>

            {/* Section 1: Introduction */}
            <section id="introduction" className="policy-section">
              <h2>1. Introduction</h2>
              <p>
                Expanded Chess operates Expanded Chess, a chess variant gaming platform accessible
                at <a href="https://www.expandedchess.com">https://www.expandedchess.com</a> (the "Service").
                This Privacy Policy describes how we collect, use, process, and disclose your personal
                information in connection with your use of the Service.
              </p>
              <p>
                By using the Service, you acknowledge that you have read and understood this Privacy
                Policy. If you do not agree with our practices, please do not use our Services.
                If you still have questions or concerns, please contact us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>.
              </p>
              <div className="info-box">
                <strong>Data Controller:</strong><br />
                Expanded Chess<br />
                Box 125, 1000 S Hwy 395, Hermiston, OR 97838<br />
                Email: <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>
              </div>
            </section>

            {/* Section 2: Information We Collect */}
            <section id="information-collected" className="policy-section">
              <h2>2. Information We Collect</h2>

              <h3>2.1 Information You Provide</h3>
              <p>We collect information you provide directly to us when you register on the Services,
              express an interest in obtaining information about us or our products and Services,
              when you participate in activities on the Services, or otherwise when you contact us, including:</p>

              <h4>Account Information:</h4>
              <ul>
                <li>Username;</li>
                <li>Email address;</li>
                <li>Password;</li>
                <li>Date of birth (for age verification only).</li>
              </ul>

              <h4>Profile and Game Settings:</h4>
              <ul>
                <li>Display preferences;</li>
                <li>Game configuration preferences;</li>
                <li>Custom variants you create.</li>
              </ul>

              <h4>Communications:</h4>
              <ul>
                <li>Messages you send via our contact form;</li>
                <li>Reports you submit about other users;</li>
                <li>Other communications with us.</li>
              </ul>

              <h4>Game Data:</h4>
              <ul>
                <li>Moves made during games;</li>
                <li>Game outcomes;</li>
                <li>Ratings, rankings, and statistics;</li>
                <li>Game history.</li>
              </ul>

              <div className="info-box">
                <strong>Sensitive Information:</strong> We do not process sensitive personal information
                (such as racial or ethnic origins, sexual orientation, religious beliefs, health data,
                biometric data, or genetic data).
              </div>

              <h3>2.2 Information Collected Automatically</h3>
              <p>When you access or use the Service, we automatically collect certain technical and usage information, including:</p>

              <h4>Device Information:</h4>
              <ul>
                <li>Browser type and version;</li>
                <li>Operating system;</li>
                <li>Device type;</li>
                <li>Screen resolution.</li>
              </ul>

              <h4>Connection Information:</h4>
              <ul>
                <li>Internet Protocol (IP) address;</li>
                <li>Approximate location (country and region) derived from IP;</li>
                <li>Internet Service Provider (ISP).</li>
              </ul>

              <h4>Usage Information:</h4>
              <ul>
                <li>Pages visited and features used;</li>
                <li>Time and date of visits;</li>
                <li>Time spent on pages;</li>
                <li>Referring URLs.</li>
              </ul>

              <h4>Technical Logs:</h4>
              <ul>
                <li>Error logs and diagnostic information;</li>
                <li>Security logs used for fraud detection and abuse prevention.</li>
              </ul>

              <p>
                We treat IP addresses and certain technical identifiers as personal data where required by applicable law.
                Like many businesses, we also collect information through cookies and similar technologies.
              </p>

              <h3>2.3 Information from Third Parties</h3>
              <p>
                We do not collect information from third parties such as public databases, marketing partners,
                or social media platforms. We may receive limited aggregated information from advertising
                partners to measure ad performance, such as impressions and click-through rates, without
                identifying you individually.
              </p>

              <p>
                All personal information that you provide to us must be true, complete, and accurate,
                and you must notify us of any changes to such personal information.
              </p>
            </section>

            {/* Section 3: How We Use Your Information */}
            <section id="how-we-use" className="policy-section">
              <h2>3. How We Use Your Information</h2>
              <p>We process your information to provide, improve, and administer our Services,
              communicate with you, for security and fraud prevention, and to comply with law.
              We may also process your information for other purposes only with your prior explicit consent.</p>

              <p><strong>We process your personal information for a variety of reasons, depending on how you interact with our Services, including:</strong></p>

              <h3>3.1 Providing and Improving the Service</h3>
              <ul>
                <li>To facilitate account creation and authentication and otherwise manage user accounts;</li>
                <li>Enabling gameplay features, including multiplayer, AI opponents, and custom variants;</li>
                <li>Storing and displaying your game history and rankings;</li>
                <li>Customizing the Service based on your preferences;</li>
                <li>Monitoring and improving functionality and performance.</li>
              </ul>

              <h3>3.2 Communication</h3>
              <ul>
                <li>Responding to your inquiries, feedback, and support requests;</li>
                <li>To request feedback and to contact you about your use of our Services;</li>
                <li>Sending service-related notifications, such as password reset emails, security alerts, or policy updates.</li>
              </ul>
              <p>
                To send you marketing and promotional communications if this is in accordance with your
                marketing preferences. You can opt out of our marketing emails at any time (see Section 9).
              </p>

              <h3>3.3 Safety, Security, and Enforcement</h3>
              <ul>
                <li>To protect our Services as part of our efforts to keep our Services safe and secure, including fraud monitoring and prevention;</li>
                <li>Enforcing our Terms of Service and community guidelines;</li>
                <li>Investigating and responding to user reports or complaints;</li>
                <li>Protecting the rights, property, and safety of users, Expanded Chess, and third parties.</li>
              </ul>

              <h3>3.4 Legal Compliance</h3>
              <ul>
                <li>Complying with applicable laws and regulations;</li>
                <li>Responding to lawful requests from government authorities or courts;</li>
                <li>Maintaining records as required by law.</li>
              </ul>

              <h3>3.5 Advertising and Analytics</h3>
              <ul>
                <li>To deliver targeted advertising tailored to your interests, location, and more;</li>
                <li>To identify usage trends so we can improve our Services;</li>
                <li>To determine the effectiveness of our marketing and promotional campaigns.</li>
              </ul>
              <p>
                We do not sell your personal information in the traditional sense (such as exchanging
                lists of names for money). However, under some privacy laws—such as the California
                Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA)—our
                sharing of information in connection with advertising may be considered a "sale" or
                "sharing" of personal information for cross-context behavioral advertising.
              </p>
              <p>
                Where required by law (for example, in the EU/EEA/UK), we will obtain your consent
                before using cookies or other tracking technologies for personalized advertising.
              </p>

              <h3>3.6 Vital Interests</h3>
              <p>
                We may process your information when necessary to save or protect an individual's
                vital interest, such as to prevent harm.
              </p>
            </section>

            {/* Section 4: Advertising and Marketing Partners */}
            <section id="advertising" className="policy-section">
              <h2>4. Advertising and Marketing Partners</h2>
              <p>
                We may partner with third-party advertising networks and providers to display
                advertisements on the Service and on other websites or services. These partners
                may use cookies, pixels, device identifiers, and similar technologies to collect
                or receive information about your use of the Service and other online activities
                over time, including:
              </p>
              <ul>
                <li>IP address and approximate location;</li>
                <li>Browser and device information;</li>
                <li>Pages visited and actions taken;</li>
                <li>Time and date of visits;</li>
                <li>Inferred interests based on browsing behavior.</li>
              </ul>
              <p>This information may be used for:</p>
              <ul>
                <li>Delivering ads tailored to your interests;</li>
                <li>Measuring ad performance (e.g., impressions and clicks);</li>
                <li>Limiting how often you see particular ads;</li>
                <li>Detecting and preventing fraudulent or abusive activity.</li>
              </ul>
              <p>
                Each advertising partner processes your data in accordance with its own privacy policy.
                We encourage you to review the privacy policies of our advertising partners to learn
                more about their data practices and how to exercise your rights directly with them.
              </p>
              <p>
                To the extent these online tracking technologies are deemed to be a "sale"/"sharing"
                (which includes targeted advertising, as defined under the applicable laws) under
                applicable US state laws, you can opt out of these online tracking technologies by
                submitting a request as described in Section 10 "US State Privacy Rights" below.
              </p>
            </section>

            {/* Section 5: Legal Basis for Processing */}
            <section id="legal-basis" className="policy-section">
              <h2>5. Legal Basis for Processing</h2>
              <p>
                We only process your personal information when we believe it is necessary and we have
                a valid legal reason (i.e., legal basis) to do so under applicable law, like with your
                consent, to comply with laws, to provide you with services to enter into or fulfill our
                contractual obligations, to protect your rights, or to fulfill our legitimate business interests.
              </p>

              <h3>5.1 If You Are Located in the EU or UK</h3>
              <p>
                The General Data Protection Regulation (GDPR) and UK GDPR require us to explain the
                valid legal bases we rely on in order to process your personal information:
              </p>

              <h4>Consent</h4>
              <p>
                We may process your information if you have given us permission (i.e., consent) to use
                your personal information for a specific purpose. You can withdraw your consent at any time.
              </p>

              <h4>Contract Performance</h4>
              <p>
                Processing is necessary to provide the Service under our Terms of Service, including:
              </p>
              <ul>
                <li>Account creation and authentication;</li>
                <li>Enabling gameplay and related features;</li>
                <li>Providing customer support.</li>
              </ul>

              <h4>Legitimate Interests</h4>
              <p>
                Processing is necessary for our legitimate interests, provided those interests are
                not overridden by your rights and freedoms, such as:
              </p>
              <ul>
                <li>Send users information about special offers and discounts on our products and services;</li>
                <li>Develop and display personalized and relevant advertising content for our users;</li>
                <li>Analyze how our Services are used so we can improve them to engage and retain users;</li>
                <li>Support our marketing activities;</li>
                <li>Diagnose problems and/or prevent fraudulent activities;</li>
                <li>Understand how our users use our products and services so we can improve user experience.</li>
              </ul>

              <h4>Legal Obligations</h4>
              <p>We may process your data where necessary to comply with legal obligations, such as:</p>
              <ul>
                <li>Cooperate with a law enforcement body or regulatory agency;</li>
                <li>Exercise or defend our legal rights;</li>
                <li>Disclose your information as evidence in litigation in which we are involved.</li>
              </ul>

              <h4>Vital Interests</h4>
              <p>
                We may process your information where we believe it is necessary to protect your vital
                interests or the vital interests of a third party, such as situations involving potential
                threats to the safety of any person.
              </p>

              <h3>5.2 If You Are Located in Canada</h3>
              <p>
                We may process your information if you have given us specific permission (i.e., express consent)
                to use your personal information for a specific purpose, or in situations where your permission
                can be inferred (i.e., implied consent). You can withdraw your consent at any time.
              </p>
              <p>In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent, including, for example:</p>
              <ul>
                <li>If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way;</li>
                <li>For investigations and fraud detection and prevention;</li>
                <li>For business transactions provided certain conditions are met;</li>
                <li>If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim;</li>
                <li>For identifying injured, ill, or deceased persons and communicating with next of kin;</li>
                <li>If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse;</li>
                <li>If it is reasonable to expect collection and use with consent would compromise the availability or the accuracy of the information;</li>
                <li>If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court relating to the production of records;</li>
                <li>If it was produced by an individual in the course of their employment, business, or profession and the collection is consistent with the purposes for which the information was produced;</li>
                <li>If the collection is solely for journalistic, artistic, or literary purposes;</li>
                <li>If the information is publicly available and is specified by the regulations.</li>
              </ul>
            </section>

            {/* Section 6: How We Share Your Information */}
            <section id="sharing" className="policy-section">
              <h2>6. How We Share Your Information</h2>

              <div className="highlight-box">
                <strong>🚫 No Traditional Sale of Personal Information</strong>
                <p>
                  We do not sell, rent, or trade your personal information to third parties in the
                  traditional sense (for example, we do not exchange lists of users for money).
                  However, our use of certain advertising and analytics partners may be considered
                  a "sale" or "sharing" of personal information under some laws (e.g., CCPA/CPRA).
                </p>
              </div>

              <p>We may need to share your personal information in the following situations:</p>

              <h3>6.1 Business Transfers</h3>
              <p>
                We may share or transfer your information in connection with, or during negotiations of,
                any merger, sale of company assets, financing, or acquisition of all or a portion of our
                business to another company.
              </p>

              <h3>6.2 Service Providers</h3>
              <p>
                We may share your information with trusted third-party service providers who assist
                us in operating the Service, such as:
              </p>
              <ul>
                <li><strong>Hosting provider:</strong> for server hosting and storage;</li>
                <li><strong>Email service provider:</strong> used to send transactional emails (e.g., password resets, verification, and notifications);</li>
                <li><strong>Security and fraud prevention providers.</strong></li>
              </ul>
              <p>
                These providers are contractually obligated to protect your information and may only
                process it according to our instructions and for the purposes described in this Privacy Policy.
              </p>

              <h3>6.3 Advertising Partners</h3>
              <p>
                We may share limited information with advertising partners to facilitate the serving
                of ads and to measure their effectiveness. This may include:
              </p>
              <ul>
                <li>IP address;</li>
                <li>Device and browser characteristics;</li>
                <li>General location information;</li>
                <li>Inferred interests based on site usage.</li>
              </ul>
              <p>
                Such sharing may be considered "selling" or "sharing" personal information under
                certain privacy laws. Your rights in this context are described in Section 10.
              </p>

              <h3>6.4 Legal Requirements and Protection of Rights</h3>
              <p>We may disclose your information if required by law or reasonably necessary to:</p>
              <ul>
                <li>Comply with legal processes, such as subpoenas or court orders;</li>
                <li>Respond to lawful requests from public or government authorities;</li>
                <li>Protect the rights, safety, or property of Expanded Chess, users, or third parties;</li>
                <li>Enforce our Terms of Service and policies;</li>
                <li>Prevent or investigate suspected wrongdoing.</li>
              </ul>

              <h3>6.5 Public Information</h3>
              <p>Certain information is visible to other users by design, including:</p>
              <ul>
                <li>Your username;</li>
                <li>Rankings and leaderboards;</li>
                <li>Game statistics;</li>
                <li>Chat messages sent during games.</li>
              </ul>
              <p>Please do not share sensitive personal information in public areas or chats.</p>
            </section>

            {/* Section 7: International Data Transfers */}
            <section id="international" className="policy-section">
              <h2>7. International Data Transfers</h2>
              <p>
                Our servers are located in the United States. If you access the Service from outside
                the United States, your information may be transferred to and processed in the United States,
                which may have data protection laws different from those in your jurisdiction.
              </p>

              <h3>7.1 Transfers from the EU/EEA/UK</h3>
              <p>
                If you are a resident in the European Economic Area (EEA), United Kingdom (UK), or Switzerland,
                then these countries may not necessarily have data protection laws or other similar laws as
                comprehensive as those in your country. However, we will take all necessary measures to protect
                your personal information in accordance with this Privacy Notice and applicable law.
              </p>
              <p>
                For users in the EU/EEA/UK, we use appropriate safeguards, such as Standard Contractual
                Clauses (SCCs), to protect personal data transferred outside these regions. You may
                contact us at <a href="mailto:info@expandedchess.com">info@expandedchess.com</a> to request
                more information about these safeguards.
              </p>
            </section>

            {/* Section 8: Data Retention */}
            <section id="retention" className="policy-section">
              <h2>8. Data Retention</h2>
              <p>
                We keep your information for as long as necessary to fulfill the purposes outlined in
                this Privacy Notice unless otherwise required by law. We will only keep your personal
                information for as long as it is necessary for the purposes set out in this Privacy Notice,
                unless a longer retention period is required or permitted by law (such as tax, accounting,
                or other legal requirements).
              </p>

              <h3>Approximate retention periods:</h3>
              <table className="retention-table">
                <thead>
                  <tr>
                    <th>Data Type</th>
                    <th>Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Date of Birth</td>
                    <td>Processed transiently for age verification, then immediately discarded</td>
                  </tr>
                  <tr>
                    <td>Account Information (username, email, hashed password)</td>
                    <td>Life of account + 7 days after deletion request</td>
                  </tr>
                  <tr>
                    <td>Game History &amp; Statistics</td>
                    <td>Life of account; anonymized/deleted within 7 days after account deletion</td>
                  </tr>
                  <tr>
                    <td>Chat Messages</td>
                    <td>90 days for moderation, security, and troubleshooting</td>
                  </tr>
                  <tr>
                    <td>Technical Logs</td>
                    <td>Typically 90 days</td>
                  </tr>
                  <tr>
                    <td>Contact Form Submissions and Support Records</td>
                    <td>Up to 2 years or until resolved</td>
                  </tr>
                </tbody>
              </table>

              <p>
                When we have no ongoing legitimate business need to process your personal information,
                we will either delete or anonymize such information, or, if this is not possible
                (for example, because your personal information has been stored in backup archives),
                then we will securely store your personal information and isolate it from any further
                processing until deletion is possible.
              </p>
            </section>

            {/* Section 9: Your Rights */}
            <section id="your-rights" className="policy-section">
              <h2>9. Your Rights</h2>

              <h3>9.1 Rights for All Users</h3>
              <p>Depending on where you are located geographically, the applicable privacy law may mean
              you have certain rights regarding your personal information:</p>
              <ul>
                <li><strong>Access:</strong> request a copy of personal information we hold about you;</li>
                <li><strong>Correction:</strong> request correction of inaccurate or incomplete information;</li>
                <li><strong>Deletion:</strong> request deletion of your personal information, subject to certain exceptions;</li>
                <li><strong>Portability:</strong> request a copy of your personal information in a structured, commonly used, machine-readable format where technically feasible.</li>
              </ul>

              <h3>9.2 Additional Rights for EU/EEA/UK Users (GDPR)</h3>
              <p>If you are in the EU/EEA, UK, or Switzerland, you may also have the right to:</p>
              <ul>
                <li><strong>Restrict Processing:</strong> request that we restrict processing in certain circumstances;</li>
                <li><strong>Object:</strong> object to processing based on legitimate interests, including profiling based on such interests;</li>
                <li><strong>Withdraw Consent:</strong> withdraw consent where processing is based on consent (for example, advertising cookies);</li>
                <li><strong>Lodge a Complaint:</strong> submit a complaint to your <a href="https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm" target="_blank" rel="noopener noreferrer">Member State data protection authority</a> or <a href="https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/" target="_blank" rel="noopener noreferrer">UK data protection authority</a>.</li>
              </ul>
              <p>
                If you are located in Switzerland, you may contact the{" "}
                <a href="https://www.edoeb.admin.ch/edoeb/en/home.html" target="_blank" rel="noopener noreferrer">
                  Federal Data Protection and Information Commissioner
                </a>.
              </p>

              <h3>9.3 Withdrawing Your Consent</h3>
              <p>
                If we are relying on your consent to process your personal information, which may be
                express and/or implied consent depending on the applicable law, you have the right to
                withdraw your consent at any time. You can withdraw your consent at any time by contacting
                us using the contact details provided in Section 17 or by updating your preferences on
                your profile page.
              </p>
              <p>
                However, please note that this will not affect the lawfulness of the processing before
                its withdrawal nor, when applicable law allows, will it affect the processing of your
                personal information conducted in reliance on lawful processing grounds other than consent.
              </p>

              <h3>9.4 Opting Out of Marketing Communications</h3>
              <p>
                You can unsubscribe from our marketing and promotional communications at any time by
                clicking on the unsubscribe link in the emails that we send, de-selecting the option
                on your profile page at Expanded Chess website, or by contacting us using the details
                in Section 17. You will then be removed from the marketing lists. However, we may still
                communicate with you — for example, to send you service-related messages that are necessary
                for the administration and use of your account, to respond to service requests, or for
                other non-marketing purposes.
              </p>

              <h3>9.5 Account Information</h3>
              <p>If you would at any time like to review or change the information in your account or terminate your account, you can:</p>
              <ul>
                <li>Log in to your account settings and update your user account;</li>
                <li>Visit your <a href="/profile">profile page</a> to manage your preferences;</li>
                <li>Contact us using the information in Section 17.</li>
              </ul>
              <p>
                Upon your request to terminate your account, we will deactivate or delete your account
                and information from our active databases. However, we may retain some information in
                our files to prevent fraud, troubleshoot problems, assist with any investigations,
                enforce our legal terms and/or comply with applicable legal requirements.
              </p>

              <h3>9.6 Cookies and Similar Technologies</h3>
              <p>
                Most Web browsers are set to accept cookies by default. If you prefer, you can usually
                choose to set your browser to remove cookies and to reject cookies. If you choose to
                remove cookies or reject cookies, this could affect certain features or services of our Services.
              </p>

              <h3>9.7 How to Exercise Your Rights</h3>
              <p>
                To exercise any of the rights described above, you can contact us by visiting{" "}
                <a href="/profile">your profile page</a>, by emailing us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>,
                or by visiting our <a href="/contact">contact page</a>. We will respond to your
                request within the timeframe required by applicable law. We may ask for additional
                information to verify your identity and prevent unauthorized requests.
              </p>
              <p>
                If you have questions or comments about your privacy rights, you may email us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>.
              </p>
            </section>

            {/* Section 10: US State Privacy Rights */}
            <section id="us-privacy" className="policy-section">
              <h2>10. US State Privacy Rights</h2>
              <p>
                If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana,
                Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
                Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access
                to and receive details about the personal information we maintain about you and how we have
                processed it, correct inaccuracies, get a copy of, or delete your personal information.
                You may also have the right to withdraw your consent to our processing of your personal information.
              </p>

              <h3>Categories of Personal Information We Collect</h3>
              <p>
                The table below shows the categories of personal information we have collected in the
                past twelve (12) months:
              </p>
              <table className="categories-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Examples</th>
                    <th>Collected</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>A. Identifiers</td>
                    <td>Contact details, such as email address, username, online identifier, IP address, and account name</td>
                    <td>YES</td>
                  </tr>
                  <tr>
                    <td>B. Personal information (California Customer Records)</td>
                    <td>Name, contact information, education, employment, employment history, and financial information</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>C. Protected classification characteristics</td>
                    <td>Gender, age, date of birth, race and ethnicity, national origin, marital status</td>
                    <td>YES (date of birth only)</td>
                  </tr>
                  <tr>
                    <td>D. Commercial information</td>
                    <td>Transaction information, purchase history, financial details, and payment information</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>E. Biometric information</td>
                    <td>Fingerprints and voiceprints</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>F. Internet or similar network activity</td>
                    <td>Browsing history, search history, online behavior, interest data, and interactions with our website</td>
                    <td>YES</td>
                  </tr>
                  <tr>
                    <td>G. Geolocation data</td>
                    <td>Device location</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>H. Audio, electronic, sensory information</td>
                    <td>Images and audio, video or call recordings</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>I. Professional or employment information</td>
                    <td>Business contact details, job title, work history</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>J. Education Information</td>
                    <td>Student records and directory information</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>K. Inferences drawn from collected information</td>
                    <td>Inferences about preferences and characteristics</td>
                    <td>NO</td>
                  </tr>
                  <tr>
                    <td>L. Sensitive personal information</td>
                    <td>Racial origin, religious beliefs, health data, biometric data</td>
                    <td>NO</td>
                  </tr>
                </tbody>
              </table>

              <p>We will use and retain the collected personal information as needed to provide the Services or for:</p>
              <ul>
                <li>Category A - Seven days after account holder requests deletion of account</li>
                <li>Category C - Seven days after account holder requests deletion of account</li>
                <li>Category F - Seven days after account holder requests deletion of account</li>
              </ul>

              <h3>Your US State Rights</h3>
              <p>You have rights under certain US state data protection laws. These rights include:</p>
              <ul>
                <li><strong>Right to know</strong> whether or not we are processing your personal data;</li>
                <li><strong>Right to access</strong> your personal data;</li>
                <li><strong>Right to correct</strong> inaccuracies in your personal data;</li>
                <li><strong>Right to request</strong> the deletion of your personal data;</li>
                <li><strong>Right to obtain a copy</strong> of the personal data you previously shared with us;</li>
                <li><strong>Right to non-discrimination</strong> for exercising your rights;</li>
                <li><strong>Right to opt out</strong> of the processing of your personal data if it is used for targeted advertising, the sale of personal data, or profiling.</li>
              </ul>

              <p>Depending upon the state where you live, you may also have the following rights:</p>
              <ul>
                <li>Right to access the categories of personal data being processed (Minnesota);</li>
                <li>Right to obtain a list of the categories of third parties to which we have disclosed personal data (California, Delaware, Maryland);</li>
                <li>Right to obtain a list of specific third parties to which we have disclosed personal data (Minnesota, Oregon);</li>
                <li>Right to limit use and disclosure of sensitive personal data (California);</li>
                <li>Right to opt out of the collection of sensitive data and personal data collected through voice or facial recognition (Florida).</li>
              </ul>

              <h3>How to Exercise Your Rights</h3>
              <p>
                To exercise these rights, you can contact us by visiting{" "}
                <a href="/profile">your profile page</a>, by emailing us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>,
                or by visiting our <a href="/contact">contact page</a>.
              </p>
              <p>
                Under certain US state data protection laws, you can designate an authorized agent to
                make a request on your behalf. We may deny a request from an authorized agent that does
                not submit proof that they have been validly authorized to act on your behalf.
              </p>

              <h3>Request Verification</h3>
              <p>
                Upon receiving your request, we will need to verify your identity to determine you are
                the same person about whom we have the information in our system. We will only use personal
                information provided in your request to verify your identity or authority to make the request.
              </p>

              <h3>Appeals</h3>
              <p>
                Under certain US state data protection laws, if we decline to take action regarding your
                request, you may appeal our decision by emailing us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>. We will inform you in
                writing of any action taken or not taken in response to the appeal, including a written
                explanation of the reasons for the decisions. If your appeal is denied, you may submit a
                complaint to your state attorney general.
              </p>

              <h3>Sales and Sharing Disclosure</h3>
              <p>
                We have not disclosed, sold, or shared any personal information to third parties for a
                business or commercial purpose in the preceding twelve (12) months. We will not sell or
                share personal information in the future belonging to website visitors, users, and other consumers.
              </p>
            </section>

            {/* Section 11: Cookies and Tracking Technologies */}
            <section id="cookies" className="policy-section">
              <h2>11. Cookies and Tracking Technologies</h2>

              <h3>11.1 What Are Cookies</h3>
              <p>
                Cookies are small data files that are placed on your computer or mobile device when you
                visit a website. Cookies are widely used by website owners to make their websites work,
                or to work more efficiently, as well as to provide reporting information.
              </p>
              <p>
                Cookies set by the website owner (in this case, Expanded Chess) are called "first-party
                cookies." Cookies set by parties other than the website owner are called "third-party
                cookies." Third-party cookies enable third-party features or functionality to be provided
                on or through the website (e.g., advertising, interactive content, and analytics). The
                parties that set these third-party cookies can recognize your computer both when it visits
                our website and when it visits certain other websites.
              </p>

              <h3>11.2 Why We Use Cookies</h3>
              <p>
                We use first-party and third-party cookies for several reasons. Some cookies are required
                for technical reasons in order for our website to operate, and we refer to these as
                "essential" or "strictly necessary" cookies. Other cookies enable us to track and target
                the interests of our users to enhance the experience on our Service. Third parties serve
                cookies through our website for advertising, analytics, and other purposes.
              </p>

              <h3>11.3 Cookies We Use</h3>
              <p>We use the following categories of cookies:</p>
              <table className="cookies-table">
                <thead>
                  <tr>
                    <th>Cookie Type</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Essential Cookies</td>
                    <td>Authentication, security, session management (required)</td>
                    <td>Session / up to 30 days</td>
                  </tr>
                  <tr>
                    <td>Preference Cookies</td>
                    <td>Remember board, theme, audio, and interface preferences</td>
                    <td>Up to 1 year</td>
                  </tr>
                  <tr>
                    <td>Analytics Cookies</td>
                    <td>Measure usage, performance, page views and feature popularity</td>
                    <td>Variable (tool-specific)</td>
                  </tr>
                  <tr>
                    <td>Marketing/Advertising</td>
                    <td>Personalized ads, frequency capping, ad performance &amp; fraud detection</td>
                    <td>Variable (partner-specific)</td>
                  </tr>
                </tbody>
              </table>
              <p>
                <strong>Essential cookies</strong> are required for the Service to function properly.
                Preference, Analytics, and Marketing cookies are non-essential and may require your
                consent depending on applicable law.
              </p>

              <h3>11.4 Managing Cookies and Consent</h3>
              <p>
                You have the right to decide whether to accept or reject cookies. You can exercise your
                cookie rights by setting your preferences in the Cookie Consent Manager. The Cookie Consent
                Manager allows you to select which categories of cookies you accept or reject. Essential
                cookies cannot be rejected as they are strictly necessary to provide you with services.
              </p>
              <p>You can also control cookies through your browser settings, which may allow you to:</p>
              <ul>
                <li>Block or delete cookies;</li>
                <li>Receive warnings before cookies are stored;</li>
                <li>Clear existing cookies.</li>
              </ul>
              <p>In addition:</p>
              <ul>
                <li>
                  <strong>For users in jurisdictions requiring consent (e.g., EU/EEA/UK):</strong> we will
                  ask for your consent before using non-essential cookies (including Analytics and Marketing
                  cookies). You may withdraw or change your consent at any time using the Cookie Settings
                  link on our website.
                </li>
                <li>
                  <strong>For California residents:</strong> you may use the "<a href="/do-not-sell">Do Not Sell or Share My Personal Information</a>" link to opt out of the sale or sharing of your personal information for advertising purposes.
                </li>
              </ul>
              <p>
                Disabling essential cookies may impair or prevent certain features of the Service from
                functioning properly. Disabling preference, analytics, or marketing cookies should not
                prevent basic access to the Service, but may affect your experience and the relevance
                of ads you see.
              </p>

              <h3>11.5 Browser Cookie Controls</h3>
              <p>
                As the means by which you can refuse cookies through your web browser controls vary from
                browser to browser, you should visit your browser's help menu for more information. The
                following links provide information about how to manage cookies on popular browsers:
              </p>
              <ul>
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                <li><a href="https://help.opera.com/en/latest/web-preferences/" target="_blank" rel="noopener noreferrer">Opera</a></li>
              </ul>

              <h3>11.6 Advertising Opt-Out Options</h3>
              <p>
                Most advertising networks offer you a way to opt out of targeted advertising. If you would
                like to find out more information, please visit:
              </p>
              <ul>
                <li><a href="http://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (US)</a></li>
                <li><a href="https://youradchoices.ca/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance of Canada</a></li>
                <li><a href="http://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer">European Interactive Digital Advertising Alliance</a></li>
              </ul>

              <h3>11.7 Web Beacons and Similar Technologies</h3>
              <p>
                Cookies are not the only way to recognize or track visitors to a website. We may use other,
                similar technologies from time to time, like web beacons (sometimes called "tracking pixels"
                or "clear gifs"). These are tiny graphics files that contain a unique identifier that enables
                us to recognize when someone has visited our website or opened an email including them.
              </p>
              <p>
                This allows us, for example, to monitor the traffic patterns of users from one page within
                a website to another, to deliver or communicate with cookies, to understand whether you have
                come to the website from an online advertisement displayed on a third-party website, to
                improve site performance, and to measure the success of email marketing campaigns. In many
                instances, these technologies are reliant on cookies to function properly, and so declining
                cookies will impair their functioning.
              </p>

              <h3>11.8 Flash Cookies</h3>
              <p>
                Websites may also use so-called "Flash Cookies" (also known as Local Shared Objects or "LSOs")
                to, among other things, collect and store information about your use of our services, fraud
                prevention, and for other site operations.
              </p>
              <p>
                If you do not want Flash Cookies stored on your computer, you can adjust the settings of your
                Flash player to block Flash Cookies storage using the tools contained in the{" "}
                <a href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html" target="_blank" rel="noopener noreferrer">Website Storage Settings Panel</a>.
                You can also control Flash Cookies by going to the{" "}
                <a href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html" target="_blank" rel="noopener noreferrer">Global Storage Settings Panel</a>{" "}
                and following the instructions. Please note that setting the Flash Player to restrict or limit
                acceptance of Flash Cookies may reduce or impede the functionality of some Flash applications.
              </p>
            </section>

            {/* Section 12: Do-Not-Track */}
            <section id="dnt" className="policy-section">
              <h2>12. Controls for Do-Not-Track Features</h2>
              <p>
                Most web browsers and some mobile operating systems and mobile applications include a
                Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference
                not to have data about your online browsing activities monitored and collected. At this stage,
                no uniform technology standard for recognizing and implementing DNT signals has been finalized.
                As such, we do not currently respond to DNT browser signals or any other mechanism that
                automatically communicates your choice not to be tracked online. If a standard for online
                tracking is adopted that we must follow in the future, we will inform you about that practice
                in a revised version of this Privacy Notice.
              </p>
              <p>
                California law requires us to let you know how we respond to web browser DNT signals.
                Because there currently is not an industry or legal standard for recognizing or honoring
                DNT signals, we do not respond to them at this time.
              </p>
            </section>

            {/* Section 13: Age Requirements */}
            <section id="age" className="policy-section">
              <h2>13. Age Requirements</h2>
              <div className="warning-box">
                <strong>⚠️ The Service is not intended for individuals under 18 years of age.</strong>
              </div>
              <p>
                We do not knowingly collect, solicit data from, or market to children under 18 years of age
                or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell
                such personal information. By using the Services, you represent that you are at least 18
                or the equivalent age as specified by law in your jurisdiction, or that you are the parent
                or guardian of such a minor and consent to such minor dependent's use of the Services.
              </p>
              <p>To help ensure compliance:</p>
              <ul>
                <li>Our registration flow requires users to input their date of birth;</li>
                <li>We use this data solely to determine age eligibility at sign-up;</li>
                <li>Once eligibility is confirmed, the specific date of birth is not stored in your profile and is not retained in active user records.</li>
              </ul>
              <p>
                If we learn that personal information from users less than 18 years of age or the equivalent
                age as specified by law in your jurisdiction has been collected, we will deactivate the account
                and take reasonable measures to promptly delete such data from our records. If you become aware
                of any data we may have collected from children under age 18 or the equivalent age as specified
                by law in your jurisdiction, please contact us at{" "}
                <a href="mailto:info@expandedchess.com">info@expandedchess.com</a>.
              </p>
            </section>

            {/* Section 14: Security Measures */}
            <section id="security" className="policy-section">
              <h2>14. Security Measures</h2>
              <p>
                We have implemented appropriate and reasonable technical and organizational security measures
                designed to protect the security of any personal information we process, including:
              </p>
              <ul>
                <li>Encryption in transit (e.g., HTTPS/TLS);</li>
                <li>Access controls and authentication;</li>
                <li>Regular security and access reviews;</li>
                <li>Limited access to personal data by authorized personnel only.</li>
              </ul>
              <p>
                However, despite our safeguards and efforts to secure your information, no electronic
                transmission over the Internet or information storage technology can be guaranteed to be
                100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other
                unauthorized third parties will not be able to defeat our security and improperly collect,
                access, steal, or modify your information. Although we will do our best to protect your
                personal information, transmission of personal information to and from our Services is at
                your own risk. You should only access the Services within a secure environment.
              </p>
            </section>

            {/* Section 15: Other Regional Privacy Rights */}
            <section id="other-regions" className="policy-section">
              <h2>15. Other Regional Privacy Rights</h2>

              <h3>15.1 Australia and New Zealand</h3>
              <p>
                We collect and process your personal information under the obligations and conditions set
                by Australia's Privacy Act 1988 and New Zealand's Privacy Act 2020.
              </p>
              <p>
                This Privacy Notice satisfies the notice requirements defined in both Privacy Acts, in
                particular: what personal information we collect from you, from which sources, for which
                purposes, and other recipients of your personal information.
              </p>
              <p>If you do not wish to provide the personal information necessary to fulfill their applicable purpose, it may affect our ability to provide our services, in particular:</p>
              <ul>
                <li>Offer you the products or services that you want;</li>
                <li>Respond to or help with your requests;</li>
                <li>Manage your account with us;</li>
                <li>Confirm your identity and protect your account.</li>
              </ul>
              <p>
                At any time, you have the right to request access to or correction of your personal information.
                You can make such a request by contacting us using the details in Section 17.
              </p>
              <p>
                If you believe we are unlawfully processing your personal information, you have the right to
                submit a complaint about a breach of the Australian Privacy Principles to the{" "}
                <a href="https://www.oaic.gov.au/privacy/privacy-complaints/lodge-a-privacy-complaint-with-us" target="_blank" rel="noopener noreferrer">
                  Office of the Australian Information Commissioner
                </a>{" "}
                and a breach of New Zealand's Privacy Principles to the{" "}
                <a href="https://www.privacy.org.nz/your-rights/making-a-complaint/" target="_blank" rel="noopener noreferrer">
                  Office of New Zealand Privacy Commissioner
                </a>.
              </p>

              <h3>15.2 Republic of South Africa</h3>
              <p>
                At any time, you have the right to request access to or correction of your personal information.
                You can make such a request by contacting us using the details in Section 17.
              </p>
              <p>
                If you are unsatisfied with the manner in which we address any complaint with regard to our
                processing of personal information, you can contact the office of the regulator:
              </p>
              <div className="info-box">
                <strong><a href="https://inforegulator.org.za/" target="_blank" rel="noopener noreferrer">The Information Regulator (South Africa)</a></strong><br />
                General enquiries: <a href="mailto:enquiries@inforegulator.org.za">enquiries@inforegulator.org.za</a><br />
                Complaints: <a href="mailto:PAIAComplaints@inforegulator.org.za">PAIAComplaints@inforegulator.org.za</a> &amp;{" "}
                <a href="mailto:POPIAComplaints@inforegulator.org.za">POPIAComplaints@inforegulator.org.za</a>
              </div>
            </section>

            {/* Section 16: Changes to This Privacy Policy */}
            <section id="changes" className="policy-section">
              <h2>16. Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time. The updated version will be indicated
              by an updated "Revised" date at the top of this Privacy Notice. When we make material changes, we will:</p>
              <ul>
                <li>Update the "Last Updated" date at the top of this Policy;</li>
                <li>Provide notice via the Service or via email to registered users, where appropriate;</li>
                <li>Obtain additional consent where required by law.</li>
              </ul>
              <p>
                We encourage you to review this Privacy Notice frequently to be informed of how we are
                protecting your information. Your continued use of the Service after the effective date
                of the revised Privacy Policy constitutes your acceptance of the updated Policy.
              </p>
            </section>

            {/* Section 17: Contact Us */}
            <section id="contact" className="policy-section">
              <h2>17. Contact Us</h2>
              <p>
                If you have questions or comments about this Privacy Policy or our data practices,
                please contact us:
              </p>
              <div className="contact-box">
                <p><strong>Expanded Chess</strong></p>
                <p>Attn: Privacy Inquiries</p>
                <p>Box 125, 1000 S Hwy 395, Hermiston, OR 97838</p>
                <p>United States</p>
                <p>Email: <a href="mailto:info@expandedchess.com">info@expandedchess.com</a></p>
              </div>
              <p>
                Users in the EU/EEA/UK may also have the right to lodge a complaint with their local
                Data Protection Authority.
              </p>
            </section>

            {/* Print Button - Bottom */}
            <div className="policy-footer">
              <button
                className="print-btn"
                onClick={() => window.print()}
              >
                🖨️ Print / Save as PDF
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
